import React, { useState, useEffect, useRef } from 'react'
import { Input } from './input'
import type { CurrencyFormat } from '../../lib/CommunityContext'
import { formatMoney } from '../../lib/money'

interface CurrencyInputProps {
  value: number
  onChange: (value: number) => void
  currencyFormat: CurrencyFormat
  disabled?: boolean
  placeholder?: string
  min?: number
  max?: number
}

export function CurrencyInput({
  value,
  onChange,
  currencyFormat,
  disabled = false,
  placeholder,
  min,
  max
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isFocused) {
      const formatted = formatCurrency(value, currencyFormat)
      setDisplayValue(formatted)
    }
  }, [value, currencyFormat, isFocused])

  /* The field shows the community's format (U4, the one formatter); an empty field for zero. */
  const formatCurrency = (amount: number, format: CurrencyFormat): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount) || numAmount === 0) return ''
    return formatMoney(numAmount, null, 'en-US', { currencyFormat: format })
  }

  const parseInput = (input: string): number => {
    const cleaned = input
      .replace(/[^\d.,]/g, '')
      .replace(',', '.')

    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : parsed
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    const cleaned = input.replace(/[^\d.,]/g, '')

    setDisplayValue(cleaned)

    const numericValue = parseInput(cleaned)

    if (min !== undefined && numericValue < min && numericValue !== 0) {
      onChange(min)
      return
    }

    if (max !== undefined && numericValue > max) {
      onChange(max)
      return
    }

    onChange(numericValue)
  }

  const handleFocus = () => {
    setIsFocused(true)
    if (value > 0) {
      setDisplayValue(value.toString())
    } else {
      setDisplayValue('')
    }
  }

  const handleBlur = () => {
    setIsFocused(false)
    const formatted = formatCurrency(value, currencyFormat)
    setDisplayValue(formatted)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
      'Home', 'End', 'Tab', 'Enter', 'Escape'
    ]

    if (allowedKeys.includes(e.key)) {
      return
    }

    if (e.ctrlKey || e.metaKey) {
      return
    }

    const isNumber = /^\d$/.test(e.key)
    const isDecimalSeparator = e.key === ',' || e.key === '.'

    if (!isNumber && !isDecimalSeparator) {
      e.preventDefault()
    }

    if (isDecimalSeparator && displayValue.includes('.')) {
      e.preventDefault()
    }

    if (isDecimalSeparator && displayValue.includes(',')) {
      e.preventDefault()
    }
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder || currencyFormat.example}
        className="font-mono"
      />
    </div>
  )
}
